import {
  CONTENT_BLOCK_TYPE,
  ContentBlockType,
  sortContentBlocksByOrder,
} from '@schema/pageContentBlock'
import { match } from 'ts-pattern'
import type { ContentSectionType } from './ContentSection'
import type { CtaBlockType } from './sections/CtaContentSection'
import type { DataSectionItemStyling } from './sections/DataContentSection'
import type { InitiativesContentSectionType } from './sections/InitiativesContentSection'
import type { PlotsContentSectionType } from './sections/PlotsContentSection'
import type { NewsContentSectionType } from './sections/NewsContentSection'
import type { StoriesContentSectionType } from './sections/StoriesContentSection'
import type { ThreadContentSectionType } from './sections/ThreadContentSection'
import type { ContactPerson } from '../contactCard/ContactCard'
import { stripHtml } from '@app/util/seo/formatMetaDescription'
import { pages, type GeographyPage } from '@app/util/links'
import {
  municipalityTabs,
  regionTabs,
  regionalActueelTabs,
  regionalActueelTabsQueryParamName,
} from '@app/pages/discover/regions/constants'

export const PAGE_CONTENT_THREAD_LIMIT = 6
export const PAGE_CONTENT_INITIATIVES_LIMIT = 3
export const PAGE_CONTENT_PLOTS_LIMIT = 3

type CreatedAtSortable = {
  createdAt?: number
}

export const selectLatestCreated = <T extends CreatedAtSortable>(
  items: T[],
  limit: number,
): T[] =>
  [...items]
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    .slice(0, limit)

export type PageContentListItemViewModel = {
  type?: string
  label?: string
  content?: string
  alert?: string
  linkText?: string
  linkUrl?: string
  backgroundColor?: string
  size?: string
  styling?: DataSectionItemStyling
  file?: {
    name?: string
    mimeType?: string
    createdAt?: number
    src?: string
  }
  image?: {
    src?: string
    altText?: string
  }
}

export type PageContentBlockViewModel = {
  id?: number
  type?: ContentBlockType
  title?: string
  body?: string
  order?: number
  truncationThreshold?: number
  listItems?: PageContentListItemViewModel[]
}

type PageContentSeeMoreLinks = {
  stories?: string
  news?: string
  thread?: string
  initiatives?: string
  plots?: string
}

export type PageContentContextData = {
  stories?: StoriesContentSectionType['items']
  news?: NewsContentSectionType['items']
  threadPosts?: ThreadContentSectionType['items']
  initiatives?: InitiativesContentSectionType['items']
  plots?: PlotsContentSectionType['items']
  titleLevel?: 'H1' | 'H2' | 'H3' | 'H4'
  geographySlug?: string
  geographyPage?: GeographyPage
  seeMoreLinkPlaceholder?: string
}

const actueelTabLink = (pagePath: string, actueelTab: string) =>
  `${pagePath}?${regionalActueelTabsQueryParamName}=${actueelTab}`

const buildRegionSeeMoreLinks = (slug: string): PageContentSeeMoreLinks => ({
  stories: actueelTabLink(
    pages.regionPageNew(slug, regionTabs.current),
    regionalActueelTabs.verhalen,
  ),
  news: actueelTabLink(
    pages.regionPageNew(slug, regionTabs.current),
    regionalActueelTabs.news,
  ),
  thread: pages.regionPageNew(slug, regionTabs.discussion),
  initiatives: pages.regionPageNew(slug, regionTabs.initiatives),
})

const buildMunicipalitySeeMoreLinks = (
  slug: string,
): PageContentSeeMoreLinks => ({
  stories: actueelTabLink(
    pages.municipalityPageNew(slug, municipalityTabs.current),
    regionalActueelTabs.verhalen,
  ),
  news: actueelTabLink(
    pages.municipalityPageNew(slug, municipalityTabs.current),
    regionalActueelTabs.news,
  ),
  thread: pages.municipalityPageNew(slug, municipalityTabs.discussion),
  initiatives: pages.municipalityPageNew(slug, municipalityTabs.initiatives),
  plots: pages.municipalityPageNew(slug, municipalityTabs.locations),
})

const placeholderSeeMoreLinks = (link: string): PageContentSeeMoreLinks => ({
  stories: link,
  news: link,
  thread: link,
  initiatives: link,
  plots: link,
})

const resolveSeeMoreLinks = (
  context: PageContentContextData,
): PageContentSeeMoreLinks | undefined => {
  if (context.geographySlug && context.geographyPage === 'region') {
    return buildRegionSeeMoreLinks(context.geographySlug)
  }
  if (context.geographySlug && context.geographyPage === 'municipality') {
    return buildMunicipalitySeeMoreLinks(context.geographySlug)
  }
  if (context.seeMoreLinkPlaceholder) {
    return placeholderSeeMoreLinks(context.seeMoreLinkPlaceholder)
  }
  return undefined
}

type PageContentBlockMappingContext = PageContentContextData & {
  seeMoreLinks?: PageContentSeeMoreLinks
}

const toCtaSize = (size?: string): CtaBlockType['size'] => {
  if (size === 'large' || size === 'medium' || size === 'small') {
    return size
  }
  return 'small'
}

const mapContactListItems = (
  listItems: PageContentListItemViewModel[],
): ContactPerson[] =>
  listItems
    .filter((item) => item.type === 'contact')
    .map((item) => ({
      name: item.label ?? '',
      role: item.content ?? '',
      buttonLabel: item.linkText ?? '',
      buttonLink: item.linkUrl ?? '',
      imageSrc: item.image?.src ?? '',
      imageAlt: item.image?.altText ?? item.label ?? '',
    }))

const mapCtaListItems = (
  listItems: PageContentListItemViewModel[],
): CtaBlockType[] =>
  listItems
    .filter((item) => item.type === 'cta')
    .map((item) => ({
      type: 'cta' as const,
      label: item.label ?? '',
      content: item.content ?? '',
      linkText: item.linkText ?? '',
      linkUrl: item.linkUrl ?? '',
      backgroundColor: item.backgroundColor ?? '#f6f6f6',
      size: toCtaSize(item.size),
      image: {
        src: item.image?.src ?? '',
        alt: item.image?.altText ?? item.label ?? '',
      },
    }))

export const mapPageContentBlockToSection = (
  block: PageContentBlockViewModel,
  context: PageContentBlockMappingContext,
): ContentSectionType | null => {
  if (!block.type) {
    return null
  }

  return match(block.type)
    .with(CONTENT_BLOCK_TYPE.text, () => {
      const title = block.title ?? ''
      const body = block.body ?? ''
      const hasBody = stripHtml(body).length > 0
      if (!hasBody) {
        return null
      }
      return {
        type: 'text' as const,
        title,
        body,
        truncationThreshold: block.truncationThreshold ?? 5,
      }
    })
    .with(CONTENT_BLOCK_TYPE.data, () => {
      const listItems = (block.listItems ?? []).map((item) => ({
        label: item.label ?? '',
        content: item.content ?? '',
        styling: item.styling,
      }))
      if (listItems.length === 0) {
        return null
      }
      return {
        type: 'data' as const,
        title: block.title ?? '',
        listItems,
      }
    })
    .with(CONTENT_BLOCK_TYPE.steps, () => {
      const listItems = (block.listItems ?? []).map((item) => ({
        content: item.content ?? '',
        alert: item.alert,
      }))
      if (listItems.length === 0) {
        return null
      }
      return {
        type: 'steps' as const,
        title: block.title ?? '',
        listItems,
      }
    })
    .with(CONTENT_BLOCK_TYPE.documents, () => {
      const listItems = (block.listItems ?? [])
        .filter((item) => item.file?.src)
        .map((item) => ({
          label: item.label ?? item.file?.name ?? '',
          file: {
            name: item.file?.name ?? '',
            mimeType: item.file?.mimeType ?? '',
            createdAt: item.file?.createdAt ?? 0,
            src: item.file?.src ?? '',
          },
        }))
      if (listItems.length === 0) {
        return null
      }
      return {
        type: 'documents' as const,
        title: block.title ?? '',
        listItems,
      }
    })
    .with(CONTENT_BLOCK_TYPE.ctaGrid, () => {
      const listItems = mapCtaListItems(block.listItems ?? [])
      if (listItems.length === 0) {
        return null
      }
      return {
        type: 'ctaGrid' as const,
        title: block.title ?? '',
        titleLevel: context.titleLevel,
        listItems,
      }
    })
    .with(CONTENT_BLOCK_TYPE.faqs, () => {
      const listItems = (block.listItems ?? []).map((item) => ({
        label: item.label ?? '',
        content: item.content ?? '',
      }))
      if (listItems.length === 0) {
        return null
      }
      return {
        type: 'faqs' as const,
        title: block.title ?? '',
        listItems,
      }
    })
    .with(CONTENT_BLOCK_TYPE.stories, () => {
      const items = context.stories ?? []
      if (items.length === 0) {
        return null
      }
      return {
        type: 'stories' as const,
        title: block.title ?? '',
        pageSlug: context.geographySlug,
        geographyPage: context.geographyPage,
        titleLevel: context.titleLevel,
        seeMoreLink: context.seeMoreLinks?.stories,
        items,
      }
    })
    .with(CONTENT_BLOCK_TYPE.news, () => {
      const items = context.news ?? []
      if (items.length === 0) {
        return null
      }
      return {
        type: 'news' as const,
        title: block.title ?? '',
        pageSlug: context.geographySlug,
        geographyPage: context.geographyPage,
        titleLevel: context.titleLevel,
        seeMoreLink: context.seeMoreLinks?.news,
        items,
      }
    })
    .with(CONTENT_BLOCK_TYPE.thread, () => {
      const items = selectLatestCreated(
        context.threadPosts ?? [],
        PAGE_CONTENT_THREAD_LIMIT,
      )
      if (items.length === 0) {
        return null
      }
      return {
        type: 'thread' as const,
        title: block.title ?? '',
        titleLevel: context.titleLevel,
        seeMoreLink: context.seeMoreLinks?.thread,
        items,
      }
    })
    .with(CONTENT_BLOCK_TYPE.initiatives, () => {
      const items = (context.initiatives ?? []).slice(
        0,
        PAGE_CONTENT_INITIATIVES_LIMIT,
      )
      if (items.length === 0) {
        return null
      }
      return {
        type: 'initiatives' as const,
        title: block.title ?? '',
        titleLevel: context.titleLevel,
        seeMoreLink: context.seeMoreLinks?.initiatives,
        items,
      }
    })
    .with(CONTENT_BLOCK_TYPE.plots, () => {
      const items = (context.plots ?? []).slice(0, PAGE_CONTENT_PLOTS_LIMIT)
      if (items.length === 0) {
        return null
      }
      return {
        type: 'plots' as const,
        title: block.title ?? '',
        titleLevel: context.titleLevel,
        seeMoreLink: context.seeMoreLinks?.plots,
        items,
      }
    })
    .with(CONTENT_BLOCK_TYPE.contactPanel, () => {
      const listItems = mapContactListItems(block.listItems ?? [])
      if (listItems.length === 0) {
        return null
      }
      return {
        type: 'contactPanel' as const,
        title: block.title ?? '',
        body: block.body,
        listItems,
      }
    })
    .otherwise((): ContentSectionType | null => null)
}

export const mapPageContentBlocksToSections = (
  blocks: PageContentBlockViewModel[],
  context: PageContentContextData = {},
): ContentSectionType[] => {
  const seeMoreLinks = resolveSeeMoreLinks(context)
  const mappingContext: PageContentBlockMappingContext = seeMoreLinks
    ? { ...context, seeMoreLinks }
    : context

  return sortContentBlocksByOrder(blocks ?? [])
    .map((block) => mapPageContentBlockToSection(block, mappingContext))
    .filter((section): section is ContentSectionType => section !== null)
}

export const hasMeaningfulPageContentBlocks = (
  blocks: PageContentBlockViewModel[],
  context: PageContentContextData = {},
): boolean =>
  sortContentBlocksByOrder(blocks ?? []).some((block) => {
    if (!block.type) {
      return stripHtml(block.body ?? '').length > 0
    }
    return mapPageContentBlockToSection(block, context) !== null
  })
